'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import {
  Radar,
  Coins,
  Fingerprint,
  Send,
  ArrowUpRight,
  EyeOff,
  Database,
  Gift,
  Clock,
} from 'lucide-react';
import { LINK_GUARD_BOT_URL } from '@/lib/silkai';
import type { LinkGuardStats } from '@/lib/web3/stats';

const TOOLS = [
  {
    href: '/token-check',
    icon: Coins,
    name: 'Token Check',
    tag: 'Rug & honeypot',
    desc: 'Paste a token mint and get a risk verdict from live liquidity, holder count, organic-activity score, dev concentration and on-chain mint & freeze authority.',
    sources: 'Dexscreener · Jupiter · on-chain',
    cta: 'Check a token',
    featured: true,
  },
  {
    href: '/link-guard',
    icon: Radar,
    name: 'Link Guard',
    tag: 'Drainer & phishing',
    desc: 'Paste any Solana dApp link. We flag drainers, typosquats and lure pages before you connect your wallet.',
    sources: 'MetaMask · ScamSniffer feeds',
    cta: 'Scan a link',
    featured: false,
  },
  {
    href: '/address-check',
    icon: Fingerprint,
    name: 'Address Inspector',
    tag: 'Anti-poisoning',
    desc: 'Validate a Solana address and catch poisoning lookalikes that copy the first and last characters of yours.',
    sources: 'On-chain account reads',
    cta: 'Inspect an address',
    featured: false,
  },
];

const TRUST = [
  { icon: EyeOff, label: 'No wallet connection' },
  { icon: Gift, label: 'Free, no sign-up' },
  { icon: Database, label: 'Live data, refreshed hourly' },
];

function fmtCompact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return `${n}`;
}

function timeAgo(iso: string | null): string {
  if (!iso) return 'continuously';
  const diff = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(diff) || diff < 0) return 'just now';
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  return `${Math.floor(mins / 60)}h ago`;
}

export default function HubExperience({ stats }: { stats: LinkGuardStats | null }) {
  const [revealed, setRevealed] = useState(false);
  const [introGone, setIntroGone] = useState(false);

  useEffect(() => {
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    if (reduce) {
      setRevealed(true);
      setIntroGone(true);
      return;
    }

    const revealTimer = setTimeout(() => setRevealed(true), 1700);
    const goneTimer = setTimeout(() => setIntroGone(true), 2300);
    return () => {
      clearTimeout(revealTimer);
      clearTimeout(goneTimer);
    };
  }, []);

  const skip = () => {
    setRevealed(true);
    setIntroGone(true);
  };

  const revealStyle = (i: number) => ({ transitionDelay: `${i * 90}ms` });

  return (
    <div className="relative">
      {/* Ambient background */}
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 h-[420px] w-[820px] max-w-[140vw] rounded-full bg-rose-500/10 blur-[120px]" />
        <div className="absolute top-1/3 right-0 h-[320px] w-[320px] rounded-full bg-sky-500/5 blur-[120px]" />
      </div>

      {/* Intro animation */}
      {!introGone && (
        <div
          onClick={skip}
          className={`fixed inset-0 z-[100000] flex flex-col items-center justify-center bg-black ${
            revealed ? 'hub-overlay-out' : ''
          }`}
        >
          <div className="relative flex items-center justify-center">
            <span className="absolute h-28 w-28 rounded-full border border-rose-500/40 hub-ring" />
            <span
              className="absolute h-28 w-28 rounded-full border border-rose-500/30 hub-ring"
              style={{ animationDelay: '0.6s' }}
            />
            <div className="hub-logo-in relative">
              <div className="absolute inset-0 -z-10 blur-2xl bg-rose-500/30 rounded-full" />
              <Image
                src="/brand/logo-512.png"
                alt="Silker AI"
                width={88}
                height={88}
                priority
                className="rounded-2xl"
              />
            </div>
          </div>
          <p className="hub-text-in mt-8 text-xs sm:text-sm font-medium uppercase text-neutral-400">
            Silker Security Hub
          </p>
        </div>
      )}

      {/* Hub content */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12">
        <div
          className={`hub-reveal ${revealed ? 'is-on' : ''} text-center`}
          style={revealStyle(0)}
        >
          <div className="flex justify-center">
            <div className="relative">
              <div className="absolute inset-0 -z-10 blur-xl bg-rose-500/20 rounded-full" />
              <Image
                src="/brand/logo-512.png"
                alt="Silker AI"
                width={60}
                height={60}
                className="rounded-2xl"
              />
            </div>
          </div>
          <h1 className="mt-5 text-3xl sm:text-4xl font-semibold tracking-tight">
            <span className="bg-gradient-to-r from-white via-rose-100 to-white bg-clip-text text-transparent">
              Silker Security Hub
            </span>
          </h1>
          <p className="mt-4 text-base text-neutral-400 max-w-xl mx-auto">
            Free Solana safety checks. Verify a link, token or wallet address in
            seconds - no wallet connection, no sign-up.
          </p>

          <div className="mt-6 flex flex-wrap justify-center gap-2">
            {TRUST.map((t) => (
              <span
                key={t.label}
                className="inline-flex items-center gap-1.5 border border-white/10 bg-white/[0.03] px-3 py-1.5 text-xs text-neutral-300"
              >
                <t.icon className="w-3.5 h-3.5 text-rose-400" />
                {t.label}
              </span>
            ))}
          </div>
        </div>

        {/* Live trust strip */}
        {stats && (
          <div
            className={`hub-reveal ${revealed ? 'is-on' : ''} mt-10 relative border border-white/10 bg-neutral-900/50 p-5 sm:p-6`}
            style={revealStyle(1)}
          >
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-rose-500/50 to-transparent" />
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <span className="inline-flex items-center gap-2 text-xs font-medium text-emerald-400">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60 animate-ping" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                </span>
                Live threat intelligence
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs text-neutral-500">
                <Clock className="w-3.5 h-3.5" /> Synced {timeAgo(stats.loadedAt)}
              </span>
            </div>
            <div className="mt-5 grid grid-cols-3 gap-4">
              <div>
                <p className="text-2xl sm:text-3xl font-bold tabular-nums text-rose-300">
                  {fmtCompact(stats.blocked)}
                </p>
                <p className="mt-1 text-[10px] sm:text-[11px] uppercase tracking-wider text-neutral-500">
                  Drainer domains tracked
                </p>
              </div>
              <div>
                <p className="text-2xl sm:text-3xl font-bold tabular-nums text-emerald-300">
                  {fmtCompact(stats.allowed)}
                </p>
                <p className="mt-1 text-[10px] sm:text-[11px] uppercase tracking-wider text-neutral-500">
                  Trusted domains
                </p>
              </div>
              <div>
                <p className="text-2xl sm:text-3xl font-bold tabular-nums text-amber-300">
                  {fmtCompact(stats.fuzzy)}
                </p>
                <p className="mt-1 text-[10px] sm:text-[11px] uppercase tracking-wider text-neutral-500">
                  Typosquat patterns
                </p>
              </div>
            </div>
            <p className="mt-4 text-[11px] text-neutral-600">
              Data: Dexscreener · Jupiter · Solana RPC · MetaMask eth-phishing-detect · ScamSniffer
            </p>
          </div>
        )}

        {/* Tools */}
        <div className="mt-6 flex items-end justify-between">
          <h2 className="text-xs uppercase tracking-[0.2em] text-neutral-500">Choose a check</h2>
        </div>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {TOOLS.map((t, i) => (
            <Link
              key={t.href}
              href={t.href}
              style={revealStyle(i + 2)}
              className={`hub-reveal ${revealed ? 'is-on' : ''} group relative flex flex-col border bg-gradient-to-b from-neutral-900/70 to-neutral-900/20 p-6 transition-all hover:-translate-y-0.5 ${
                t.featured
                  ? 'border-rose-500/30 hover:border-rose-500/60'
                  : 'border-white/10 hover:border-rose-500/40'
              }`}
            >
              {t.featured && (
                <span className="absolute right-4 top-4 text-[9px] uppercase tracking-wider text-rose-300 border border-rose-500/30 bg-rose-500/10 px-1.5 py-0.5">
                  Multi-source
                </span>
              )}
              <span className="inline-flex h-11 w-11 items-center justify-center border border-rose-500/30 bg-rose-500/10">
                <t.icon className="w-5 h-5 text-rose-400" />
              </span>
              <h3 className="mt-4 text-lg font-semibold text-white">{t.name}</h3>
              <span className="text-xs text-neutral-500">{t.tag}</span>
              <p className="mt-2 text-sm text-neutral-400 flex-1">{t.desc}</p>
              <p className="mt-3 text-[10px] uppercase tracking-wider text-neutral-600">
                {t.sources}
              </p>
              <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-rose-300 group-hover:text-rose-200">
                {t.cta}
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            </Link>
          ))}
        </div>

        {/* Telegram bot */}
        <div
          className={`hub-reveal ${revealed ? 'is-on' : ''} mt-4 border border-white/10 bg-gradient-to-r from-sky-500/[0.06] to-transparent p-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between`}
          style={revealStyle(5)}
        >
          <div className="flex items-start gap-4">
            <span className="inline-flex h-11 w-11 items-center justify-center border border-sky-500/30 bg-sky-500/10 shrink-0">
              <Send className="w-5 h-5 text-sky-400" />
            </span>
            <div>
              <h2 className="text-lg font-semibold text-white">Run it inside Telegram</h2>
              <p className="mt-1 text-sm text-neutral-400 max-w-md">
                Drop the bot into your Solana group - it auto-checks every link,
                token mint and address members paste.
              </p>
            </div>
          </div>
          <a
            href={LINK_GUARD_BOT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-none border border-white/15 text-sm font-medium text-white hover:border-white/40 transition-colors shrink-0"
          >
            <Send className="w-4 h-4" /> Open Telegram bot
          </a>
        </div>
      </section>
    </div>
  );
}
